import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, MapPin, Package, Calendar, ArrowRight, Truck, Shield, Euro, CheckCircle2, Route, Sparkles } from "lucide-react";
import { PromoBanner } from "@/components/PromoBanner";
import { PriceEstimator } from "@/components/PriceEstimator";

const CARGO = ["Möbel", "Umzugsgut", "Einzelstück", "Kartons", "Sonstiges"];

function SearchMask() {
  const nav = useNavigate();
  const [f, setF] = useState({ von: "", nach: "", cargo: "Möbel", volumen: "", from: "", to: "", flexible: false });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const go = () => {
    const p = new URLSearchParams({
      von: f.von, nach: f.nach, cargo: f.cargo, volumen: f.volumen,
      from: f.from, to: f.to, flexible: f.flexible ? "1" : "",
    });
    nav(`/anfrage?${p.toString()}`);
  };
  return (
    <div className="eb-card p-5 sm:p-6 bg-[#161F32]/95 backdrop-blur border-slate-700/60 shadow-2xl" data-testid="hero-search-mask">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="eb-label">Von</label>
          <div className="relative mt-1.5">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FF5A1F]" />
            <input className="eb-input pl-9" placeholder="Ort / PLZ" value={f.von} onChange={set("von")} data-testid="search-von-input" />
          </div>
        </div>
        <div>
          <label className="eb-label">Nach</label>
          <div className="relative mt-1.5">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400" />
            <input className="eb-input pl-9" placeholder="Ort / PLZ" value={f.nach} onChange={set("nach")} data-testid="search-nach-input" />
          </div>
        </div>
        <div>
          <label className="eb-label">Was soll transportiert werden?</label>
          <select className="eb-input mt-1.5" value={f.cargo} onChange={set("cargo")} data-testid="search-cargo-select">
            {CARGO.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="eb-label">Volumen (m³)</label>
          <div className="relative mt-1.5">
            <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input className="eb-input pl-9" type="number" step="0.5" placeholder="z.B. 6" value={f.volumen} onChange={set("volumen")} data-testid="search-volumen-input" />
          </div>
        </div>
        <div>
          <label className="eb-label">Zeitraum von</label>
          <input className="eb-input mt-1.5" type="date" value={f.from} onChange={set("from")} data-testid="search-from-input" />
        </div>
        <div>
          <label className="eb-label">bis</label>
          <input className="eb-input mt-1.5" type="date" value={f.to} onChange={set("to")} data-testid="search-to-input" />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-5">
        <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer select-none">
          <input type="checkbox" checked={f.flexible} onChange={set("flexible")} className="w-4 h-4 accent-[#FF5A1F]" data-testid="search-flexible-checkbox" />
          Ich bin beim Termin flexibel
        </label>
        <button onClick={go} className="eb-btn-primary w-full sm:w-auto" data-testid="search-submit-btn">
          <Search className="w-4 h-4" /> Passende Transporte finden
        </button>
      </div>
    </div>
  );
}

const TRUST = [
  { icon: Truck, title: "Professionelle Transportpartner", text: "Geprüfte Speditionen und Umzugsunternehmen führen deinen Transport durch." },
  { icon: Euro, title: "Transparente Angebote", text: "Klare Preisaufschlüsselung – du siehst immer, was du zahlst." },
  { icon: Shield, title: "Versicherte Transporte", text: "Transporte über abgesicherte Partner mit Transportversicherung." },
  { icon: Package, title: "Nur benötigten Laderaum zahlen", text: "Bezahle nur den Platz, den dein Transportgut wirklich braucht." },
];

const STEPS = [
  { n: "01", title: "Anfrage stellen", text: "Start, Ziel, Transportgut und Volumen in wenigen Schritten angeben." },
  { n: "02", title: "Passende Beiladungen", text: "Unser regelbasiertes Matching findet Fahrten mit freiem Laderaum auf deiner Route." },
  { n: "03", title: "Angebot buchen", text: "Transparentes Angebot auswählen und sicher online bezahlen." },
];

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/11053644/pexels-photo-11053644.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F17]/70 via-[#0B0F17]/85 to-[#0B0F17]" />
        </div>
        <div className="relative eb-container py-16 lg:py-24">
          <div className="max-w-3xl eb-fade-up">
            <span className="eb-badge bg-[#FF5A1F]/15 text-[#FF8A5F] mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Beiladung einfach buchen
            </span>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Freien Laderaum<br /><span className="text-[#FF5A1F]">einfach nutzen.</span>
            </h1>
            <div className="mt-5"><PromoBanner compact /></div>
            <p className="text-lg text-slate-300 mt-6 max-w-2xl">
              Finde passende Beiladungen für Möbel, Umzugsgut und Einzelstücke – deutschlandweit.
              Spare gegenüber einem eigenen Transport, ohne einen ganzen LKW zu bezahlen.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/anfrage" className="eb-btn-primary" data-testid="hero-cta-finden">
                <Search className="w-4 h-4" /> Transport finden
              </Link>
              <Link to="/partner" className="eb-btn-outline" data-testid="hero-cta-anbieten">
                <Truck className="w-4 h-4" /> Freien Laderaum anbieten
              </Link>
            </div>
          </div>
          <div className="mt-12 max-w-4xl">
            <SearchMask />
          </div>
        </div>
      </section>

      <section className="eb-container -mt-4 mb-4"><PriceEstimator /></section>

      {/* How it works */}
      <section className="eb-section eb-container">
        <div className="max-w-2xl">
          <span className="eb-label text-[#FF5A1F]">So funktioniert's</span>
          <h2 className="font-head text-3xl lg:text-4xl font-bold mt-2">In drei Schritten zur Beiladung</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {STEPS.map((s) => (
            <div key={s.n} className="eb-card eb-card-hover p-7" data-testid={`step-${s.n}`}>
              <div className="font-mono text-[#FF5A1F] text-sm mb-3">{s.n}</div>
              <h3 className="font-head text-xl font-semibold mb-2">{s.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust */}
      <section className="eb-section bg-[#111827] border-y border-slate-800/80">
        <div className="eb-container">
          <div className="max-w-2xl">
            <span className="eb-label text-[#FF5A1F]">Vertrauen & Sicherheit</span>
            <h2 className="font-head text-3xl lg:text-4xl font-bold mt-2">Warum Easy Beiladung</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {TRUST.map((t) => (
              <div key={t.title} className="eb-card p-6" data-testid={`trust-${t.title.slice(0,6)}`}>
                <div className="w-11 h-11 rounded-lg bg-[#FF5A1F]/10 flex items-center justify-center mb-4">
                  <t.icon className="w-5 h-5 text-[#FF5A1F]" />
                </div>
                <h3 className="font-head font-semibold mb-1.5">{t.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner CTA */}
      <section className="eb-section eb-container">
        <div className="eb-card p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-72 h-72 bg-[#FF5A1F]/10 blur-3xl rounded-full" />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="eb-badge bg-emerald-500/15 text-emerald-400 mb-4"><Route className="w-3.5 h-3.5" /> Für Transportunternehmen</span>
              <h2 className="font-head text-3xl font-bold mb-3">Freie Kapazitäten sinnvoll füllen</h2>
              <p className="text-slate-400 mb-6">Veröffentliche deine geplanten Fahrten und freien Laderaum. Reduziere Leerfahrten und erreiche neue Kunden – ohne Aufwand.</p>
              <div className="space-y-2.5">
                {["Eigene Fahrten & Kapazitäten einstellen","Automatisches Matching mit Anfragen","Transparente Provision & Auszahlung"].map((x)=>(
                  <div key={x} className="flex items-center gap-2.5 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" /> {x}
                  </div>
                ))}
              </div>
              <Link to="/partner" className="eb-btn-primary mt-7" data-testid="partner-cta-btn">
                Partner werden <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="hidden lg:block">
              <img src="https://images.pexels.com/photos/20097991/pexels-photo-20097991.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Transport auf der Autobahn" className="rounded-xl border border-slate-700 object-cover w-full h-72" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
